import { useState } from 'react'
import { useNavigate } from 'react-router'
import { FormDialog } from '@/components/common'
import { DatePicker } from '@/components/ui/date-picker'
import { Textarea } from '@/components/ui/textarea'
import { EXIT_TYPE_LABEL, useExitMutations } from '@/features/exit'
import type { ExitType } from '@/features/exit'
import { toApiProblem } from '@/lib/api/client'
import { ChoiceField, Field } from './EmployeeFields'

const EXIT_TYPES = (Object.keys(EXIT_TYPE_LABEL) as ExitType[])
  .map((value) => ({ value, label: EXIT_TYPE_LABEL[value] }))

/**
 * Taking somebody out of the active directory.
 *
 * <p>The action is deliberately not a delete, and the screen says so. An employment that has
 * happened cannot be un-happened: there are payslips, attendance days and leave balances
 * pointing at it, and removing the row would leave every one of them resolving to nobody. What
 * ends an employment is an exit case, which records the last working day, the settlement and
 * the clearance — so that is what this opens.
 *
 * <p>The server refuses the delete for exactly this reason, naming the exit case. Offering the
 * exit case here means somebody who came looking for "delete" finds the thing they actually
 * needed rather than an error.
 */
export function DeactivateEmployeeDialog({
  employeeId, employeeName, open, onOpenChange,
}: {
  employeeId: string
  employeeName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const { openCase } = useExitMutations()
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState({
    exitType: 'RESIGNATION',
    reason: '',
    noticeGivenOn: '',
    lastWorkingDay: '',
  })

  async function submit() {
    setError(null)
    try {
      const created = await openCase.mutateAsync({
        employeeId,
        exitType: draft.exitType,
        reason: draft.reason || null,
        noticeGivenOn: draft.noticeGivenOn || null,
        lastWorkingDay: draft.lastWorkingDay || null,
      })
      onOpenChange(false)
      // Straight to the case: the clearance and the settlement are the rest of this job, and
      // leaving somebody on the profile with a case they cannot see is how one gets forgotten.
      void navigate(`/exit/${created.id}`)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'The exit case could not be opened.')
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`End ${employeeName}'s employment`}
      description="This opens an exit case. Nothing is deleted — their pay history, attendance and documents stay exactly where they are, and they leave the active directory when the last working day passes."
      submitLabel="Open the exit case"
      loading={openCase.isPending}
      error={error}
      onSubmit={() => { void submit() }}
    >
      <div className="grid gap-4">
        <ChoiceField id="exit-type" label="Reason for leaving" required choices={EXIT_TYPES}
          emptyLabel="Choose one"
          value={draft.exitType} onChange={(value) => setDraft({ ...draft, exitType: value })} />
        <Field id="notice-given" label="Notice given on"
          hint="Leave blank if no notice was served — the notice rules then compute from the last working day."
        >
          <DatePicker id="notice-given" value={draft.noticeGivenOn}
            onChange={(event) => setDraft({ ...draft, noticeGivenOn: event.target.value })} />
        </Field>
        <Field id="last-working-day" label="Last working day"
          hint="Can be set later. The settlement and the final payslip both read this date."
        >
          <DatePicker id="last-working-day" value={draft.lastWorkingDay}
            onChange={(event) => setDraft({ ...draft, lastWorkingDay: event.target.value })} />
        </Field>
        <Field id="exit-reason" label="Notes">
          <Textarea id="exit-reason" rows={3} value={draft.reason}
            onChange={(event) => setDraft({ ...draft, reason: event.target.value })} />
        </Field>
      </div>
    </FormDialog>
  )
}
